import { RoleId } from '@common/constants';
import type { PermissionEntry, RoleWithPermissions } from '@common/types/roles';
import CheckIcon from '@mui/icons-material/Check';
import {
    Box,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';
import { fetchRolePermissions } from '@/services/RoleService';

// Everyone first, then job roles, then admins in increasing power. Roles not listed go last
const ROLE_ORDER: number[] = [
    RoleId.BASIC,
    RoleId.LAWYER,
    RoleId.CLERK,
    RoleId.NOTARY,
    RoleId.ORGANIZATIONAL_ADMIN,
    RoleId.ADMIN,
    RoleId.SUPER_ADMIN,
];

function roleRank(roleId: number) {
    const index = ROLE_ORDER.indexOf(roleId);
    return index === -1 ? ROLE_ORDER.length : index;
}

function groupByResource(permissions: PermissionEntry[]) {
    const groups = new Map<string, PermissionEntry[]>();
    for (const permission of permissions) {
        groups.set(permission.resource, [...(groups.get(permission.resource) ?? []), permission]);
    }
    return [...groups.entries()];
}

const CODE_NOTES = [
    {
        title: 'Every user has Basic.',
        body: 'The app gives it alongside any other role, so the light ticks show what each role gets from Basic.',
    },
    {
        title: 'Super Admin is mostly checked by name in code, not through this table.',
        body: "That covers every firm's data, not just their own (apart from a private firm's client data without a grant); the Law Firms page and firm settings, including making a firm private; the insights dashboard; correcting billed prices; adding and editing retainer agreements and a template's default retainer; APS prefill tags; default questions; voiding signed retainers; giving roles beyond the standard ones and moving users between firms; and the Lexo Admin settings, including this page.",
    },
    {
        title: 'Organizational Admin has the same permissions here as Lawyer, Clerk and Notary.',
        body: "Its only extra power is in code: giving and ending Lexo's access to a private firm, under Settings, Lexo Access. Lawyers, Clerks and Notaries can manage their firm's users, including making someone an Organizational Admin.",
    },
    {
        title: "The Matter permissions aren't checked anywhere.",
        body: 'Matters are checked with the Prospect permissions.',
    },
    {
        title: 'Later: proper role-based access control.',
        body: 'The plan is to move the checks above into permissions, so this table is the whole story. For example, an access:grant permission that only Organizational Admins have, instead of checking the role by name.',
    },
];

export default function RolesPermissionsTab() {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['rolePermissions'],
        queryFn: fetchRolePermissions,
        staleTime: 5 * 60_000,
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    if (isError || !data) {
        return <Typography color="error">{"Couldn't load roles and permissions."}</Typography>;
    }

    const roles = [...data.roles].sort((a, b) => roleRank(a.roleId) - roleRank(b.roleId));
    const basicPermissionIds = new Set(
        roles.find((role) => role.roleId === RoleId.BASIC)?.permissionIds ?? [],
    );
    const groups = groupByResource(data.permissions);

    const renderCell = (role: RoleWithPermissions, permission: PermissionEntry) => {
        if (role.permissionIds.includes(permission.permissionId)) {
            return <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />;
        }
        if (role.roleId !== RoleId.BASIC && basicPermissionIds.has(permission.permissionId)) {
            return (
                <Tooltip title="From Basic, which every user has">
                    <CheckIcon sx={{ fontSize: 18, color: 'neutral.N_200' }} />
                </Tooltip>
            );
        }
        return null;
    };

    return (
        <Stack spacing={3}>
            <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                {"What each role is allowed to do, from the database's "}
                <code>role_permissions</code>
                {
                    ' table. Much of what Super Admins and Organizational Admins can do is still checked in code instead: see the notes below the table.'
                }
            </Typography>
            <TableContainer
                sx={{ border: '1px solid', borderColor: 'neutral.N_100', borderRadius: '8px' }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Permission</TableCell>
                            {roles.map((role) => (
                                <TableCell key={role.roleId} align="center">
                                    {role.name ?? role.roleId}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map(([resource, permissions]) => (
                            <Fragment key={resource}>
                                <TableRow>
                                    <TableCell
                                        colSpan={roles.length + 1}
                                        sx={{ bgcolor: 'neutral.N_50', fontWeight: 600 }}
                                    >
                                        {resource}
                                    </TableCell>
                                </TableRow>
                                {permissions.map((permission) => (
                                    <TableRow key={permission.permissionId}>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {permission.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'neutral.N_500' }}
                                            >
                                                {permission.resourceCode}
                                            </Typography>
                                        </TableCell>
                                        {roles.map((role) => (
                                            <TableCell key={role.roleId} align="center">
                                                {renderCell(role, permission)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack spacing={1.5} sx={{ maxWidth: '900px' }}>
                <Typography variant="h3">Checked in code</Typography>
                {CODE_NOTES.map((note) => (
                    <Typography key={note.title} variant="body2" sx={{ color: 'neutral.N_700' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {note.title}
                        </Box>{' '}
                        {note.body}
                    </Typography>
                ))}
            </Stack>
        </Stack>
    );
}
